<div class="flex gap-6 py-4">
  <!-- Types tree -->
  <div class="w-5/12 min-w-[20rem]">
    <div class="flex items-center justify-between mb-3">
      <h2 class="text-lg font-semibold">Types de modèles</h2>
      <button
        pButton
        type="button"
        icon="pi pi-plus"
        label="Nouveau type racine"
        size="small"
        [disabled]="saving"
        (click)="openCreateType(null)"
      ></button>
    </div>

    <div class="flex items-center gap-2 mb-3">
      <p-toggleswitch
        inputId="show-deleted-gain-templates"
        [ngModel]="showDeleted"
        (onChange)="onShowDeletedChange($event.checked)"
      ></p-toggleswitch>
      <label for="show-deleted-gain-templates" class="text-sm"
        >Afficher les éléments supprimés</label
      >
    </div>

    <div *ngIf="loading" class="flex justify-center p-4">
      <p-progressSpinner
        [style]="{ width: '40px', height: '40px' }"
      ></p-progressSpinner>
    </div>

    <p-tree
      [value]="nodes"
      selectionMode="single"
      [(selection)]="selectedNode"
      [metaKeySelection]="false"
      (onNodeSelect)="onNodeSelect($event)"
      (onNodeUnselect)="onNodeUnselect()"
      (onNodeExpand)="onNodeExpand($event.node)"
      (onNodeCollapse)="onNodeCollapse($event.node)"
      styleClass="w-full"
    >
      <ng-template let-node pTemplate="default">
        <!-- Type node -->
        <div
          *ngIf="node.data.kind === 'type'"
          class="flex items-center gap-2 w-full"
        >
          <i class="pi pi-folder"></i>
          <span class="truncate">{{ node.label }}</span>
          <span class="ml-auto flex gap-1">
            <button
              pButton
              type="button"
              icon="pi pi-plus"
              class="p-button-text p-button-sm"
              pTooltip="Ajouter un sous-type"
              [disabled]="saving || hasActiveTemplates(node.data.type.uid)"
              (click)="
                $event.stopPropagation(); openCreateType(node.data.type.uid)
              "
            ></button>
            <button
              pButton
              type="button"
              icon="pi pi-pencil"
              class="p-button-text p-button-sm"
              pTooltip="Renommer"
              [disabled]="saving"
              (click)="$event.stopPropagation(); openRenameType(node.data.type)"
            ></button>
            <button
              pButton
              type="button"
              icon="pi pi-trash"
              class="p-button-text p-button-danger p-button-sm"
              pTooltip="Supprimer"
              [disabled]="saving"
              (click)="
                $event.stopPropagation(); confirmDeleteType(node.data.type)
              "
            ></button>
          </span>
        </div>

        <!-- Template node -->
        <div
          *ngIf="node.data.kind === 'template'"
          class="flex items-center gap-2 w-full"
          [ngClass]="{ 'opacity-60': node.data.template.isDeleted }"
        >
          <i class="pi pi-calculator"></i>
          <span
            class="truncate"
            [ngClass]="{ 'line-through': node.data.template.isDeleted }"
            >{{ node.label }}</span
          >
          <p-tag
            [value]="'v' + node.data.template.version"
            severity="info"
          ></p-tag>
          <p-tag
            *ngIf="node.data.template.isDeleted"
            value="Supprimé"
            severity="danger"
          ></p-tag>
          <i
            *ngIf="
              node.data.template.deletedParameters.length ||
              node.data.template.unknownParameters.length
            "
            class="pi pi-exclamation-triangle text-orange-500"
            pTooltip="Cette formule référence des paramètres supprimés ou inexistants"
          ></i>
        </div>
      </ng-template>
      <ng-template pTemplate="empty">
        <p class="text-gray-500 p-2">
          Aucun type défini. Créez un type racine pour commencer.
        </p>
      </ng-template>
    </p-tree>
  </div>

  <!-- Editor -->
  <div class="flex-1 min-w-0">
    <p *ngIf="mode === 'none'" class="text-gray-500 p-4">
      Sélectionnez un type feuille pour créer un modèle de formule, ou un modèle
      existant pour le consulter ou le modifier. Les modèles ne peuvent être
      rattachés qu'aux types sans sous-type.
    </p>

    <ng-container *ngIf="mode !== 'none'">
      <div class="flex items-center justify-between mb-3 gap-3">
        <h2 class="text-lg font-semibold truncate">
          {{
            mode === "new"
              ? "Nouveau modèle dans « " + currentType?.name + " »"
              : "Modèle de formule"
          }}
        </h2>
        <div *ngIf="mode === 'edit'" class="flex items-center gap-2">
          <label for="gain-template-version" class="text-sm">Version</label>
          <p-select
            inputId="gain-template-version"
            [options]="versionOptions"
            optionLabel="label"
            optionValue="value"
            [ngModel]="viewed?.uid"
            (onChange)="onVersionChange($event.value)"
            appendTo="body"
          ></p-select>
        </div>
      </div>

      <p-message
        *ngIf="viewed?.isDeleted"
        severity="error"
        styleClass="mb-3"
        text="Ce modèle est supprimé : lecture seule."
      ></p-message>
      <p-message
        *ngIf="readOnly && !viewed?.isDeleted"
        severity="info"
        styleClass="mb-3"
        text="Version antérieure : lecture seule. Sélectionnez la version courante pour modifier."
      ></p-message>
      <p-message
        *ngIf="
          viewed &&
          (viewed.deletedParameters.length || viewed.unknownParameters.length)
        "
        severity="warn"
        styleClass="mb-3"
      >
        <span *ngIf="viewed.deletedParameters.length">
          Paramètre(s) supprimé(s) :
          {{ viewed.deletedParameters.join(", ") }}.
        </span>
        <span *ngIf="viewed.unknownParameters.length">
          Paramètre(s) inexistant(s) :
          {{ viewed.unknownParameters.join(", ") }}.
        </span>
      </p-message>

      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <label for="gain-template-name">Nom</label>
          <input
            id="gain-template-name"
            type="text"
            pInputText
            maxlength="255"
            [readonly]="readOnly"
            [(ngModel)]="form.name"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label for="gain-template-description">Description</label>
          <input
            id="gain-template-description"
            type="text"
            pInputText
            maxlength="1000"
            [readonly]="readOnly"
            [(ngModel)]="form.description"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label for="gain-template-equation">Formule</label>
          <textarea
            #equationInput
            id="gain-template-equation"
            rows="5"
            class="p-inputtext w-full font-mono"
            placeholder="Ex. : [Taux] * [Volume] / 100"
            [readonly]="readOnly"
            [ngModel]="form.equation"
            (ngModelChange)="onEquationChange($event)"
          ></textarea>
          <ul
            *ngIf="!readOnly && validation.errors.length"
            class="text-sm text-red-600 list-disc pl-5"
          >
            <li *ngFor="let error of validation.errors">{{ error }}</li>
          </ul>
        </div>

        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium">
            Paramètres disponibles
            <span *ngIf="!readOnly" class="font-normal text-gray-500"
              >(cliquer pour insérer à la position du curseur)</span
            >
          </span>
          <p *ngIf="parameters.length === 0" class="text-sm text-gray-500">
            Aucun paramètre défini.
          </p>
          <div class="flex flex-wrap gap-2">
            <button
              *ngFor="let parameter of parameters"
              pButton
              type="button"
              size="small"
              severity="secondary"
              [label]="parameter.name"
              [pTooltip]="parameter.description || ''"
              [disabled]="readOnly"
              (click)="insertParameter(parameter.name)"
            ></button>
          </div>
        </div>

        <div class="flex gap-2 justify-end">
          <button
            *ngIf="canDeleteTemplate"
            pButton
            type="button"
            label="Supprimer"
            icon="pi pi-trash"
            class="p-button-danger p-button-text"
            [disabled]="saving"
            (click)="confirmDeleteTemplate()"
          ></button>
          <button
            pButton
            type="button"
            [label]="
              mode === 'new' ? 'Créer' : 'Enregistrer une nouvelle version'
            "
            icon="pi pi-save"
            [disabled]="!canSave"
            (click)="saveTemplate()"
          ></button>
        </div>
      </div>
    </ng-container>
  </div>
</div>

<p-confirmDialog [key]="confirmKey"></p-confirmDialog>

<!-- Type dialog -->
<p-dialog
  [header]="typeDialogMode === 'create' ? 'Nouveau type' : 'Modifier le type'"
  [(visible)]="typeDialogVisible"
  [modal]="true"
  [style]="{ width: '30rem' }"
>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-1">
      <label for="gain-type-name">Nom</label>
      <input
        id="gain-type-name"
        type="text"
        pInputText
        maxlength="255"
        [(ngModel)]="typeForm.name"
        (keydown.enter)="saveType()"
      />
    </div>
    <div class="flex flex-col gap-1">
      <label for="gain-type-description">Description</label>
      <input
        id="gain-type-description"
        type="text"
        pInputText
        maxlength="1000"
        [(ngModel)]="typeForm.description"
      />
    </div>
  </div>
  <ng-template #footer>
    <button
      pButton
      type="button"
      label="Annuler"
      class="p-button-text"
      (click)="typeDialogVisible = false"
    ></button>
    <button
      pButton
      type="button"
      label="Enregistrer"
      [disabled]="!canSaveType"
      (click)="saveType()"
    ></button>
  </ng-template>
</p-dialog>
